/**
 * Payment Gateway Abstraction Layer
 * Provides a unified interface for different payment processors
 * Enables easy switching between Stripe, PayPal, etc.
 */

// Base interface for all payment processors
export class PaymentProcessor {
    constructor(config) {
        this.config = config;
    }

    // Initialize the payment processor
    async initialize() {
        throw new Error('initialize() must be implemented by payment processor');
    }

    // Create payment intent/session
    async createPaymentIntent(amount, currency, metadata) {
        throw new Error('createPaymentIntent() must be implemented by payment processor');
    }

    // Confirm payment with payment method
    async confirmPayment(paymentIntentId, paymentMethodData, idempotencyKey = null) {
        throw new Error('confirmPayment() must be implemented by payment processor');
    }

    // Generate idempotency key for payment requests
    generateIdempotencyKey() {
        // Generate a unique key using timestamp and random elements
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 15);
        return `idem_${timestamp}_${random}`;
    }

    // Handle 3D Secure or additional authentication
    async handleNextAction(paymentIntent) {
        throw new Error('handleNextAction() must be implemented by payment processor');
    }

    // Validate payment method
    validatePaymentMethod(paymentMethodData) {
        throw new Error('validatePaymentMethod() must be implemented by payment processor');
    }

    // Get supported payment methods
    getSupportedMethods() {
        throw new Error('getSupportedMethods() must be implemented by payment processor');
    }
}

// Mock payment processor for development/testing
export class MockPaymentProcessor extends PaymentProcessor {
    constructor(config = {}) {
        super(config);
        this.isInitialized = false;
    }

    async initialize() {
        console.log('Initializing Mock Payment Processor...');
        this.isInitialized = true;
        return true;
    }

    async createPaymentIntent(amount, currency = 'USD', metadata = {}) {
        if (!this.isInitialized) {
            throw new Error('Payment processor not initialized');
        }

        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 500));

        const paymentIntent = {
            id: `pi_mock_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            amount: Math.round(amount * 100), // Convert to cents
            currency: currency.toLowerCase(),
            status: 'requires_payment_method',
            clientSecret: `pi_mock_secret_${Date.now()}`,
            metadata
        };

        console.log('Mock Payment Intent created:', paymentIntent);
        return paymentIntent;
    }

    async confirmPayment(paymentIntentId, paymentMethodData, idempotencyKey = null) {
        if (!this.isInitialized) {
            throw new Error('Payment processor not initialized');
        }

        // Log idempotency key for debugging
        if (idempotencyKey) {
            console.log('Mock Payment: Using idempotency key:', idempotencyKey);
        }

        // Simulate payment processing delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Simulate different outcomes based on card number
        const cardNumber = paymentMethodData.card?.number || '4242424242424242';

        if (cardNumber.includes('4000000000000002')) {
            // Simulate card declined
            return {
                paymentIntent: {
                    id: paymentIntentId,
                    status: 'requires_payment_method'
                },
                error: {
                    type: 'card_error',
                    code: 'card_declined',
                    message: 'Your card was declined.',
                    decline_code: 'generic_decline'
                }
            };
        }

        if (cardNumber.includes('4000000000003220')) {
            // Simulate 3D Secure required
            return {
                paymentIntent: {
                    id: paymentIntentId,
                    status: 'requires_action',
                    next_action: {
                        type: 'use_stripe_sdk'
                    }
                }
            };
        }

        // Simulate successful payment
        return {
            paymentIntent: {
                id: paymentIntentId,
                status: 'succeeded',
                amount: 5000, // $50.00
                currency: 'usd',
                charges: {
                    data: [{
                        id: `ch_mock_${Date.now()}`,
                        payment_method: {
                            card: {
                                brand: 'visa',
                                last4: cardNumber.slice(-4)
                            }
                        }
                    }]
                }
            }
        };
    }

    async handleNextAction(paymentIntent) {
        // Simulate 3D Secure authentication
        await new Promise(resolve => setTimeout(resolve, 2000));

        return {
            paymentIntent: {
                ...paymentIntent,
                status: 'succeeded'
            }
        };
    }

    validatePaymentMethod(paymentMethodData) {
        const errors = {};

        if (!paymentMethodData.card) {
            errors.card = 'Card information is required';
            return { isValid: false, errors };
        }

        const { number, expiryMonth, expiryYear, cvc } = paymentMethodData.card;

        // Basic card number validation
        if (!number || number.length < 13 || number.length > 19) {
            errors.cardNumber = 'Please enter a valid card number';
        }

        // Expiry validation
        if (!expiryMonth || !expiryYear) {
            errors.expiry = 'Please enter a valid expiry date';
        } else {
            const now = new Date();
            const expiry = new Date(2000 + parseInt(expiryYear), parseInt(expiryMonth) - 1);
            if (expiry < now) {
                errors.expiry = 'Card has expired';
            }
        }

        // CVC validation
        if (!cvc || cvc.length < 3 || cvc.length > 4) {
            errors.cvc = 'Please enter a valid CVC';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    getSupportedMethods() {
        return [
            {
                id: 'card',
                name: 'Credit/Debit Card',
                icon: '💳',
                description: 'Visa, Mastercard, American Express'
            },
            {
                id: 'mock_wallet',
                name: 'Mock Digital Wallet',
                icon: '📱',
                description: 'Simulated digital wallet payment'
            }
        ];
    }
}

// Stripe payment processor (for future implementation)
export class StripePaymentProcessor extends PaymentProcessor {
    constructor(config) {
        super(config);
        this.stripe = null;
        this.isInitialized = false;
    }

    async initialize() {
        if (typeof window === 'undefined') {
            throw new Error('Stripe can only be initialized in browser environment');
        }

        try {
            // Dynamically import Stripe
            const { loadStripe } = await import('@stripe/stripe-js');
            this.stripe = await loadStripe(this.config.publishableKey);

            if (!this.stripe) {
                throw new Error('Failed to load Stripe');
            }

            this.isInitialized = true;
            console.log('Stripe Payment Processor initialized');
            return true;
        } catch (error) {
            console.error('Failed to initialize Stripe:', error);
            throw error;
        }
    }

    async createPaymentIntent(amount, currency = 'USD', metadata = {}) {
        if (!this.isInitialized) {
            throw new Error('Stripe not initialized');
        }

        try {
            const response = await fetch('/api/payments/create-intent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({
                    amount: Math.round(amount * 100), // Convert to cents
                    currency: currency.toLowerCase(),
                    metadata
                })
            });

            if (!response.ok) {
                throw new Error('Failed to create payment intent');
            }

            return await response.json();
        } catch (error) {
            console.error('Error creating payment intent:', error);
            throw error;
        }
    }

    async confirmPayment(clientSecret, paymentMethodData, idempotencyKey = null) {
        if (!this.isInitialized) {
            throw new Error('Stripe not initialized');
        }

        try {
            const confirmOptions = {
                payment_method: paymentMethodData
            };

            // Add idempotency key to Stripe options if provided
            if (idempotencyKey) {
                confirmOptions.idempotency_key = idempotencyKey;
            }

            return await this.stripe.confirmCardPayment(clientSecret, confirmOptions);
        } catch (error) {
            console.error('Error confirming payment:', error);
            throw error;
        }
    }

    async handleNextAction(paymentIntent) {
        if (!this.isInitialized) {
            throw new Error('Stripe not initialized');
        }

        try {
            return await this.stripe.handleCardAction(paymentIntent.client_secret);
        } catch (error) {
            console.error('Error handling next action:', error);
            throw error;
        }
    }

    validatePaymentMethod(paymentMethodData) {
        // Stripe handles validation through its Elements
        // This is mainly for additional custom validation
        return { isValid: true, errors: {} };
    }

    getSupportedMethods() {
        return [
            {
                id: 'card',
                name: 'Credit/Debit Card',
                icon: '💳',
                description: 'Visa, Mastercard, American Express, and more'
            },
            {
                id: 'apple_pay',
                name: 'Apple Pay',
                icon: '🍎',
                description: 'Pay with Touch ID or Face ID'
            },
            {
                id: 'google_pay',
                name: 'Google Pay',
                icon: 'G',
                description: 'Pay with Google Pay'
            }
        ];
    }
}

// Payment gateway factory
export class PaymentGatewayFactory {
    static create(type, config) {
        switch (type) {
            case 'stripe':
                return new StripePaymentProcessor(config);
            case 'mock':
                return new MockPaymentProcessor(config);
            default:
                throw new Error(`Unsupported payment processor type: ${type}`);
        }
    }
}

// Default payment processor configuration
export const getDefaultPaymentProcessor = () => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    // Use mock processor in development or if Stripe is not configured
    if (isDevelopment || !stripeKey) {
        return PaymentGatewayFactory.create('mock', {});
    }

    return PaymentGatewayFactory.create('stripe', {
        publishableKey: stripeKey
    });
};

export default PaymentGatewayFactory;
